import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { factory } from "../../api/apiFactory";
import { showNotification } from "./notificationSlice";

const qcApi = factory.get("qc");

const getTodayDate = () => dayjs().format("YYYY-MM-DD");

const initialState = {
  qc_table: {
    data: [],
    loading: false,
    total: 0,
    filters: {
      skip: 0,
      take: 25,
      page: 0,
      search: "",
      task_type_name: "",
      task_status: "",
      is_evaluated: null,
      is_solved: null,
      is_matching_price: null,
      is_answered: null,
      rating: null,
      start_date: getTodayDate(),
      end_date: getTodayDate(),
    },
    dialog: false,
  },
  dashboard: {
    data: null,
    loading: false,
    filters: {
      start_date: getTodayDate(),
      end_date: getTodayDate(),
      responsible_id: null,
      cabinet_id: null,
      city_id: null,
    },
  },
};

const qcSlice = createSlice({
  name: "qc",
  initialState,
  reducers: {
    setQcTable: (state, { payload }) => {
      const payloadData = payload?.data;
      if (Array.isArray(payloadData)) {
        state.qc_table.data = payloadData;
        state.qc_table.total = payload?.total ?? payloadData.length;
      } else if (Array.isArray(payloadData?.data)) {
        state.qc_table.data = payloadData.data;
        state.qc_table.total = payloadData.total ?? payloadData.data.length;
      } else if (Array.isArray(payload)) {
        state.qc_table.data = payload;
        state.qc_table.total = payload.length;
      } else {
        state.qc_table.data = [];
        state.qc_table.total = 0;
      }
    },
    setLoading: (state, { payload }) => {
      if (state[payload]) {
        state[payload].loading = !state[payload].loading;
      }
    },
    setFilters: (state, { payload }) => {
      state.qc_table.filters = {
        ...state.qc_table.filters,
        ...payload,
      };
    },
    setFiltersDialog: (state) => {
      state.qc_table.dialog = !state.qc_table.dialog;
    },
    resetFilters: (state) => {
      state.qc_table.filters = {
        ...initialState.qc_table.filters,
        start_date: getTodayDate(),
        end_date: getTodayDate(),
      };
    },
    setDashboardData: (state, { payload }) => {
      state.dashboard.data = payload?.data || payload;
    },
    setDashboardLoading: (state, { payload }) => {
      state.dashboard.loading =
        payload !== undefined ? payload : !state.dashboard.loading;
    },
    setDashboardFilters: (state, { payload }) => {
      state.dashboard.filters = {
        ...state.dashboard.filters,
        ...payload,
      };
    },
    resetDashboardFilters: (state) => {
      state.dashboard.filters = {
        ...initialState.dashboard.filters,
        start_date: getTodayDate(),
        end_date: getTodayDate(),
      };
    },
  },
});

export const {
  setQcTable,
  setLoading,
  setFilters,
  resetFilters,
  setFiltersDialog,
  setDashboardData,
  setDashboardLoading,
  setDashboardFilters,
  resetDashboardFilters,
} = qcSlice.actions;

export default qcSlice.reducer;

const getQcTasks =
  (params = null) =>
    async (dispatch, getState) => {
      try {
        dispatch(setLoading("qc_table"));
        const filters = getState().qc.qc_table.filters;
        const filters_params = {
          ...filters,
          ...params,
        };

        let res;
        try {
          res = await qcApi.taskIndex(filters_params);
        } catch (getErr) {
          if (getErr?.response?.status === 405 && qcApi.taskIndexPost) {
            res = await qcApi.taskIndexPost(filters_params);
          } else {
            throw getErr;
          }
        }

        dispatch(setQcTable(res.data));
        dispatch(setLoading("qc_table"));
      } catch (err) {
        dispatch(setLoading("qc_table"));
        dispatch(
          showNotification({
            message:
              err?.response?.data?.errors?.[0] ||
              err?.response?.data?.message ||
              "حدث خطأ أثناء جلب بيانات رقابة الجودة",
            type: "error",
          }),
        );
        throw new Error(err);
      }
    };

const evaluateTask = (data) => async (dispatch) => {
  try {
    await qcApi.evaluate(data);
    dispatch(
      showNotification({
        message: data.id ? "تم تعديل التقييم بنجاح" : "تم حفظ التقييم بنجاح",
        type: "success",
      }),
    );
    return true;
  } catch (err) {
    dispatch(
      showNotification({
        message:
          err?.response?.data?.errors?.[0] ||
          err?.response?.data?.message ||
          "حدث خطأ أثناء حفظ التقييم",
        type: "error",
      }),
    );
    throw new Error(err);
  }
};

const getQcDashboard =
  (params = null) =>
  async (dispatch, getState) => {
    try {
      dispatch(setDashboardLoading(true));
      const filters = getState().qc.dashboard.filters;
      const merged = {
        ...filters,
        ...params,
      };
      const filters_params = {
        ...merged,
        city_id: merged.city_id?.id || merged.city_id || null,
        cabinet_id: merged.cabinet_id?.id || merged.cabinet_id || null,
        responsible_id: merged.responsible_id?.id || merged.responsible_id || null,
      };

      const res = await qcApi.getDashboard(filters_params);
      dispatch(setDashboardData(res.data));
      dispatch(setDashboardLoading(false));
      return res.data;
    } catch (err) {
      dispatch(setDashboardLoading(false));
      dispatch(
        showNotification({
          message:
            err?.response?.data?.errors?.[0] ||
            err?.response?.data?.message ||
            "حدث خطأ أثناء جلب إحصائيات لوحة تحكم الجودة",
          type: "error",
        }),
      );
      throw new Error(err);
    }
  };

export const QcActions = {
  getQcTasks,
  evaluateTask,
  getQcDashboard,
};

